/**
 * surgeCracks.ts — G8 DYNAMIC CRACKS (grounded-world-plan.md §G8)
 *
 * STORY.md §The ground: creatures come OUT of the ground — emergence CREATES
 * a crack, and existing cracks are live proximity spawn points. The purple
 * cracks aren't decoration, they're exit wounds.
 *
 * This is Atlas's half of the LOCKED API contract (BOARD 8889d4b):
 *   queryCracksNear / createSurgeCrack / canCrackAt — Bram's spawn tick
 * consumes these. Spawn budgets, proximity triggers and per-enemy emergence
 * anims are Bram's side and do NOT live here.
 *
 * Design law (§G8 d): dynamic cracks are ENTITIES drawn over the baked
 * ground every frame — capped + culled, NEVER chunk-bake invalidation.
 * Visuals reuse the exact baked pipeline (drawVeinBase / glow sprite /
 * pulse personalities) so a dynamic crack is indistinguishable from a baked
 * one once fully grown: recessed dark fracture + crisp steady neon core +
 * soft pulsing glow, 2.5D ground squash inherited from the vein generator.
 *
 * Growth (§G8 c): new cracks tear open fast then settle — visual only.
 * `radius` reports the FULL logical radius from birth so Bram's spawn logic
 * is stable while the art grows in.
 *
 * Timebase: `createdAt` is performance.now() ms (same clock the renderer
 * gets). Baked cracks report createdAt = 0 (they predate the run).
 */

import { CHUNK_WU } from './mapChunks';
import { getMachineStations } from './paths';
import { isForestWalkBlockedAt } from './forest';
import {
  type Vein, veinsForChunk, genVeinAt, drawVeinBase,
  veinGlowSprite, veinPulseAlpha,
} from './surgeVeins';
import { propFootprintAt } from './worldProps';

export type SurgeCrackId = number;

export interface SurgeCrack {
  id: SurgeCrackId;
  x: number;
  y: number;
  radius: number;
  createdAt: number;
  source: 'baked' | 'emergence';
}

// ── module state (per run — engine calls resetSurgeCracks at run init +
// floor advance, same lifecycle as clearChunkCache) ─────────────────────────
interface DynCrack extends SurgeCrack {
  source: 'emergence';
  vein: Vein;
  /** birth timestamp for the grow-in anim (== createdAt) */
  bornMs: number;
  /** anti-clone salt used to gen the vein */
  salt: number;
  base?: { cv: HTMLCanvasElement; ox: number; oy: number } | null;
}

const MAX_DYNAMIC = 140;        // §G8 d — hard cap; oldest culled first
const GROW_MS = 1400;           // tear-open time (visual only)
const MERGE_DEFAULT_WU = 70;
const CELL_WU = 256;            // dynamic-crack spatial hash cell

let _seed = 1;
let _nextId: SurgeCrackId = 1;
let _dyn: DynCrack[] = [];
const _grid = new Map<string, DynCrack[]>();
/** stable per-vein baked entries, cached per chunk */
const _bakedByChunk = new Map<string, SurgeCrack[]>();
/** crack id → its vein geometry (baked + dynamic) — merge tests measure
 *  distance to the ACTUAL polyline, never the bbox circle */
const _veinById = new Map<SurgeCrackId, Vein>();

function _cellKey(x: number, y: number): string {
  return `${Math.floor(x / CELL_WU)},${Math.floor(y / CELL_WU)}`;
}
function _gridAdd(c: DynCrack): void {
  const k = _cellKey(c.x, c.y);
  const arr = _grid.get(k);
  if (arr) arr.push(c); else _grid.set(k, [c]);
}
function _gridRemove(c: DynCrack): void {
  const arr = _grid.get(_cellKey(c.x, c.y));
  if (!arr) return;
  const i = arr.indexOf(c);
  if (i >= 0) arr.splice(i, 1);
}

/** Fresh store for a fresh world (run start / floor advance). */
export function resetSurgeCracks(seed: number): void {
  _seed = seed;
  _nextId = 1;
  _dyn = [];
  _grid.clear();
  _bakedByChunk.clear();
  _veinById.clear();
}

/** Is (x,y) within `wu` of the vein's actual crack lines (any segment)? */
function _veinWithinWu(v: Vein, x: number, y: number, wu: number): boolean {
  if (x < v.minX - wu || x > v.maxX + wu || y < v.minY - wu || y > v.maxY + wu) return false;
  const w2 = wu * wu;
  let prev: { x: number; y: number } | null = null;
  for (const p of v.pts) {
    if (!p) { prev = null; continue; }
    if (prev) {
      const dx = p.x - prev.x, dy = p.y - prev.y;
      const len2 = dx * dx + dy * dy;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - prev.x) * dx + (y - prev.y) * dy) / len2)) : 0;
      const ex = prev.x + dx * t - x, ey = prev.y + dy * t - y;
      if (ex * ex + ey * ey <= w2) return true;
    } else {
      const ex = p.x - x, ey = p.y - y;
      if (ex * ex + ey * ey <= w2) return true;
    }
    prev = p;
  }
  return false;
}

// ── baked cracks as queryable SurgeCracks ────────────────────────────────────
// Each baked vein cluster collapses to one circle (bbox center + half-extent).
// IDs are negative + deterministic (hash of the vein key) so they're stable
// across queries and can never collide with dynamic ids (positive).
function _bakedId(key: string): SurgeCrackId {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return -(((h >>> 1) >>> 0) + 1);
}

function _bakedForChunk(cx: number, cy: number): SurgeCrack[] {
  const key = `${cx},${cy}`;
  const hit = _bakedByChunk.get(key);
  if (hit) return hit;
  if (_bakedByChunk.size > 512) _bakedByChunk.clear();
  const out: SurgeCrack[] = veinsForChunk(cx, cy, _seed).map(v => {
    const id = _bakedId(v.key);
    _veinById.set(id, v);
    return {
      id,
      x: (v.minX + v.maxX) / 2,
      y: (v.minY + v.maxY) / 2,
      radius: Math.min(240, Math.max(30, Math.max(v.maxX - v.minX, v.maxY - v.minY) / 2)),
      createdAt: 0,
      source: 'baked' as const,
    };
  });
  _bakedByChunk.set(key, out);
  return out;
}

// ── THE LOCKED API (§G8 — signatures verbatim, Bram codes against these) ────

/**
 * Cheap spatial lookup: every crack (baked + dynamic) whose CENTER is within
 * `radiusWu` of (x,y), nearest first. `opts.max` caps the list.
 */
export function queryCracksNear(
  x: number, y: number, radiusWu: number, opts?: { max?: number },
): SurgeCrack[] {
  if (!Number.isFinite(x) || !Number.isFinite(y) || !(radiusWu > 0)) return [];
  const out: Array<{ c: SurgeCrack; d: number }> = [];
  // dynamic — spatial hash cells covering the circle
  const g0x = Math.floor((x - radiusWu) / CELL_WU), g1x = Math.floor((x + radiusWu) / CELL_WU);
  const g0y = Math.floor((y - radiusWu) / CELL_WU), g1y = Math.floor((y + radiusWu) / CELL_WU);
  for (let gy = g0y; gy <= g1y; gy++) for (let gx = g0x; gx <= g1x; gx++) {
    const arr = _grid.get(`${gx},${gy}`);
    if (!arr) continue;
    for (const c of arr) {
      const d = Math.hypot(c.x - x, c.y - y);
      if (d <= radiusWu) out.push({ c, d });
    }
  }
  // baked — chunks covering the circle PLUS one ring: baked veins are keyed
  // by the chunk that GENERATED them, but a vein can walk ~250wu outside it
  // (the bake pass sweeps dx,dy −1..1 for exactly this bleed). Without the
  // ring, a border-straddling rift is invisible to spawn queries and
  // merge-dedup. The per-crack distance check below filters the extras.
  const c0x = Math.floor((x - radiusWu) / CHUNK_WU) - 1, c1x = Math.floor((x + radiusWu) / CHUNK_WU) + 1;
  const c0y = Math.floor((y - radiusWu) / CHUNK_WU) - 1, c1y = Math.floor((y + radiusWu) / CHUNK_WU) + 1;
  for (let cy = c0y; cy <= c1y; cy++) for (let cx = c0x; cx <= c1x; cx++) {
    for (const c of _bakedForChunk(cx, cy)) {
      const d = Math.hypot(c.x - x, c.y - y);
      if (d <= radiusWu) out.push({ c, d });
    }
  }
  out.sort((a, b) => a.d - b.d);
  const max = opts?.max ?? Infinity;
  return out.slice(0, max).map(e => e.c);
}

/**
 * Emergence tore the ground here. Merges into an existing crack within
 * `mergeWithinWu` (default 70) instead of stacking duplicates — a merged
 * DYNAMIC crack widens a little (the wound reopens); a merged BAKED crack
 * is returned as-is. Otherwise a new dynamic crack entity is born (grow-in
 * anim, capped at 140 — oldest culled). Callers gate with canCrackAt first;
 * create does NOT re-check (Bram's spawn point is already ground-truthed).
 */
export function createSurgeCrack(
  x: number, y: number, opts?: { radius?: number; mergeWithinWu?: number },
): SurgeCrack {
  const mergeWu = opts?.mergeWithinWu ?? MERGE_DEFAULT_WU;
  if (mergeWu > 0) {
    // candidates: anything whose SPAN could reach mergeWu of the point
    const near = queryCracksNear(x, y, mergeWu + 260, { max: 12 });
    for (const c of near) {
      if (Math.hypot(c.x - x, c.y - y) > mergeWu + c.radius) continue;
      // §G8 contract: "within mergeWithinWu" means within mergeWu of the
      // CRACK ITSELF — measured to the vein's actual polyline, never its
      // bbox circle. A point 150wu perpendicular to a rift is clean ground:
      // a NEW crack must be born there, or an enemy emerges from unbroken
      // dirt and the exit-wounds story beat breaks.
      const v = _veinById.get(c.id);
      if (v ? !_veinWithinWu(v, x, y, mergeWu) : Math.hypot(c.x - x, c.y - y) > mergeWu) continue;
      if (c.source === 'emergence') {
        const d = _dyn.find(k => k.id === c.id);
        if (d) { d.radius = Math.min(260, d.radius * 1.05 + 3); return d; }
      }
      return c;
    }
  }
  const vein = genVeinAt(x, y, _seed, _nextId);
  const radius = opts?.radius
    ?? Math.min(240, Math.max(30, Math.max(vein.maxX - vein.minX, vein.maxY - vein.minY) / 2));
  const crack: DynCrack = {
    id: _nextId++,
    x, y, radius,
    createdAt: performance.now(),
    source: 'emergence',
    vein,
    bornMs: performance.now(),
    salt: _nextId - 1,
  };
  _dyn.push(crack);
  _gridAdd(crack);
  _veinById.set(crack.id, vein);
  if (_dyn.length > MAX_DYNAMIC) {
    const old = _dyn.shift();
    if (old) { _gridRemove(old); _veinById.delete(old.id); }
  }
  return crack;
}

/**
 * Atlas's ground-truth gate — can the ground open HERE? Bram's spawn logic
 * must check this before createSurgeCrack. Rules: no blocked forest (baked
 * canopy hides the ground), no standing-prop footprints, keep the Wild
 * Cards station pads clean. Paths/trails are fair game — the baked cracks
 * already prefer seams, and a road splitting open reads great.
 */
export function canCrackAt(x: number, y: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if (isForestWalkBlockedAt(x, y, _seed)) return false;
  if (propFootprintAt(x, y, _seed)) return false;
  for (const st of getMachineStations(_seed)) {
    if (Math.hypot(x - st.x, y - st.y) < 80) return false;
  }
  return true;
}

// ── render (engine ground pass — after baked vein glow, under standing) ─────
// Base sprite bakes at the chunk resolution (0.5 px/wu) through the SAME
// drawVeinBase pipeline, so a settled dynamic crack is pixel-for-pixel the
// baked look. Grow-in: scale + alpha ease around the emergence point.
const BASE_PX_PER_WU = 0.5;

function _baseSprite(c: DynCrack): { cv: HTMLCanvasElement; ox: number; oy: number } | null {
  if (c.base !== undefined) return c.base;
  const pad = Math.ceil(18 + c.vein.glowR);
  const w = Math.ceil((c.vein.maxX - c.vein.minX) * BASE_PX_PER_WU) + pad * 2;
  const h = Math.ceil((c.vein.maxY - c.vein.minY) * BASE_PX_PER_WU) + pad * 2;
  if (w <= 0 || h <= 0 || w > 1400 || h > 1400) { c.base = null; return null; }
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  const ctx = cv.getContext('2d');
  if (!ctx) { c.base = null; return null; }
  const lx = (wx: number) => (wx - c.vein.minX) * BASE_PX_PER_WU + pad;
  const ly = (wy: number) => (wy - c.vein.minY) * BASE_PX_PER_WU + pad;
  drawVeinBase(ctx, c.vein, _seed, lx, ly);
  c.base = { cv, ox: c.vein.minX - pad / BASE_PX_PER_WU, oy: c.vein.minY - pad / BASE_PX_PER_WU };
  return c.base;
}

function _easeOutCubic(t: number): number { const u = 1 - t; return 1 - u * u * u; }

/**
 * Draw all visible dynamic cracks. WORLD space, inside the camera transform:
 * call right after renderSurgeVeinGlow (over baked ground + glow, under
 * every standing thing).
 */
export function renderDynamicCracks(
  ctx: CanvasRenderingContext2D,
  viewLeft: number, viewTop: number, viewRight: number, viewBottom: number,
  nowMs: number,
): void {
  if (!_dyn.length) return;
  for (const c of _dyn) {
    if (c.vein.maxX < viewLeft - 60 || c.vein.minX > viewRight + 60 ||
        c.vein.maxY < viewTop - 60 || c.vein.minY > viewBottom + 60) continue;
    const age = nowMs - c.bornMs;
    const grow = _easeOutCubic(Math.min(1, age / GROW_MS));
    const s = 0.35 + 0.65 * grow;
    // ── base stack (normal composite — the crisp core must NOT additive-wash)
    const b = _baseSprite(c);
    if (b) {
      ctx.save();
      ctx.globalAlpha = Math.min(1, age / 260);
      if (s < 0.999) {
        ctx.translate(c.x, c.y);
        ctx.scale(s, s);
        ctx.translate(-c.x, -c.y);
      }
      ctx.drawImage(b.cv, b.ox, b.oy, b.cv.width / BASE_PX_PER_WU, b.cv.height / BASE_PX_PER_WU);
      ctx.restore();
    }
    // ── glow (additive, pulsing — same personalities as the baked pass,
    // plus a birth FLARE: the wound burns bright, then settles to breathe)
    const g = veinGlowSprite(c.vein);
    if (g) {
      const flare = Math.max(0, 1 - age / (GROW_MS * 1.6));
      const a = veinPulseAlpha(c.vein, nowMs);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = Math.max(0.04, Math.min(1,
        a * (0.55 + c.vein.intensity * 0.75) + flare * 0.55));
      if (s < 0.999) {
        ctx.translate(c.x, c.y);
        ctx.scale(s, s);
        ctx.translate(-c.x, -c.y);
      }
      ctx.drawImage(g.cv, g.ox, g.oy, g.cv.width / BASE_PX_PER_WU, g.cv.height / BASE_PX_PER_WU);
      ctx.restore();
    }
  }
}

/** Live dynamic-crack count (dev overlay / telemetry). */
export function dynamicCrackCount(): number { return _dyn.length; }
